/**
 * Geometry of the background selector's crop previews. Each tile shows the
 * background as one horizontal banner of the page crops it: the banner whose
 * crop changed last, so the tile follows the admin's work and keeps its shape.
 */

export type PreviewTemplate = {id: string; width: number; height: number};

export type TilePreviewLayout = {
  /** Tile size in CSS pixels. */
  tileWidth: number;
  tileHeight: number;
  /** Scale from banner pixels to tile pixels. */
  scale: number;
  /** Offset of the scaled banner inside the tile (letterbox). */
  left: number;
  top: number;
};

export const TILE_WIDTH = 90;
/** The tile shape the selector used before previews: 90 × 50. */
const TARGET_ASPECT = TILE_WIDTH / 50;
const MIN_TILE_HEIGHT = 40;
const MAX_TILE_HEIGHT = 72;

function nearestToTileShape(candidates: PreviewTemplate[]): PreviewTemplate | null {
  let best: PreviewTemplate | null = null;
  let bestDistance = Infinity;
  for (const t of candidates) {
    const distance = Math.abs(Math.log(t.width / t.height / TARGET_ASPECT));
    if (distance < bestDistance) {
      best = t;
      bestDistance = distance;
    }
  }
  return best;
}

/**
 * The banners a preview can use: the shown horizontal ones; the horizontal
 * ones of the route when none is shown; every banner of the route when the
 * route has no horizontal banner.
 */
export function previewCandidates(
  routeTemplates: PreviewTemplate[],
  shownIds: string[] | null | undefined
): PreviewTemplate[] {
  const sized = routeTemplates.filter((t) => t.width > 0 && t.height > 0);
  const horizontal = sized.filter((t) => t.width > t.height);
  const shown = new Set(shownIds ?? []);
  const shownHorizontal = horizontal.filter((t) => shown.has(t.id));
  if (shownHorizontal.length > 0) return shownHorizontal;
  if (horizontal.length > 0) return horizontal;
  return sized;
}

/**
 * The banners a preview uses when the current layout gives some banners an
 * image of their own (FigBaker's `NN_bg_MM_layout` frames): those banners of
 * the route, any orientation, the shown ones first. The tile then shows the
 * layout's image in place of the background's usual one. Empty when the
 * layout gives no banner of the route an image: use `previewCandidates`.
 */
export function layoutPreviewCandidates(
  routeTemplates: PreviewTemplate[],
  shownIds: string[] | null | undefined,
  layoutImageIds: string[]
): PreviewTemplate[] {
  const own = new Set(layoutImageIds);
  const sized = routeTemplates.filter((t) => t.width > 0 && t.height > 0 && own.has(t.id));
  const shown = new Set(shownIds ?? []);
  const shownOwn = sized.filter((t) => shown.has(t.id));
  return shownOwn.length > 0 ? shownOwn : sized;
}

/**
 * The banner of one tile:
 *   1. the candidate whose crop changed last in this session (`adjustedOrder`
 *      lists template ids, the newest last);
 *   2. else the candidate the saved default scene names (`savedId`, the
 *      `bgPreviewTemplate` field);
 *   3. else the candidate nearest to the 9:5 tile shape.
 * Null when the route has no banner with a size.
 */
export function pickTilePreviewTemplate({
  candidates,
  adjustedOrder,
  savedId,
}: {
  candidates: PreviewTemplate[];
  adjustedOrder?: string[] | null;
  savedId?: string | null;
}): PreviewTemplate | null {
  const byId = new Map(candidates.map((t) => [t.id, t]));
  for (const id of [...(adjustedOrder ?? [])].reverse()) {
    const adjusted = byId.get(id);
    if (adjusted) return adjusted;
  }
  if (savedId) {
    const saved = byId.get(savedId);
    if (saved) return saved;
  }
  return nearestToTileShape(candidates);
}

/** The newest-changed candidate of `adjustedOrder`, or null. */
export function lastAdjustedCandidate(
  candidates: PreviewTemplate[],
  adjustedOrder: string[] | null | undefined
): PreviewTemplate | null {
  const ids = new Set(candidates.map((t) => t.id));
  const id = [...(adjustedOrder ?? [])].reverse().find((x) => ids.has(x));
  return candidates.find((t) => t.id === id) ?? null;
}

/**
 * The tile height for a set of candidates: 90 px wide, in the proportion of
 * the candidate nearest to the tile shape, within 40-72 px. Every tile has
 * this height, so the grid stays even when tiles use different banners.
 */
export function tileHeightFor(candidates: PreviewTemplate[]): number | null {
  const template = nearestToTileShape(candidates);
  if (!template) return null;
  const proportional = (TILE_WIDTH * template.height) / template.width;
  return Math.round(Math.min(MAX_TILE_HEIGHT, Math.max(MIN_TILE_HEIGHT, proportional)));
}

/** The banner fitted inside a 90 px × `tileHeight` tile, centred. */
export function tilePreviewLayout(
  template: PreviewTemplate,
  tileHeight: number
): TilePreviewLayout {
  const scale = Math.min(TILE_WIDTH / template.width, tileHeight / template.height);
  return {
    tileWidth: TILE_WIDTH,
    tileHeight,
    scale,
    left: (TILE_WIDTH - template.width * scale) / 2,
    top: (tileHeight - template.height * scale) / 2,
  };
}
